/**
 * src/state/employeeVersions.js
 *
 * Per-employee ROWVERSION registry for the employees-write path
 * (ADR-0008 §2.4, EMPA-03).
 *
 * Employees moved from the shared-`state_version` tenant_state blob onto
 * dbo.tenant_employees, one row per record with its own ROWVERSION. Two admins
 * editing DIFFERENT employees now touch different rows and cannot collide;
 * two editing the SAME employee get one 409 and a banner, which is correct.
 * That second half only works if the client can say which version it is
 * replacing — so state-read hands back an `employeeVersions` map alongside the
 * state, employees-write returns the new version for every record it writes,
 * and this module is where those tokens live between the two.
 *
 * Module-level rather than React state on purpose: the token belongs to the
 * server row, not to any component, and every write path — the Add/Edit modal,
 * the Workbench's bulk actions, the lifecycle wizard — has to send the same
 * one. A token held in a component would go stale the moment a sibling
 * component saved.
 *
 * Missing token semantics, matching the server: absent means "this client has
 * never seen a version for that employee". For a record with no row yet (a
 * create, or a tenant not split into per-record rows) that is the normal case
 * and the write is accepted. For a record that DOES have a row, the server
 * refuses — a client editing an employee whose version it has never held is
 * working from a copy that predates the row, and letting it through is the
 * silent last-writer-wins this migration removes.
 */

// employeeId -> hex ROWVERSION
const versions = new Map();

/**
 * Replace what we know with the map state-read returned. A full replace, not a
 * merge: a record the server no longer reports has no row any more, and
 * keeping a token for it would send a version the server cannot match.
 */
export function adoptEmployeeVersions(map) {
  versions.clear();
  if (!map || typeof map !== 'object') return;
  for (const [id, hex] of Object.entries(map)) {
    if (typeof hex === 'string' && hex) versions.set(id, hex);
  }
}

/** Merge in the versions a write response returned for the records it wrote. */
export function mergeEmployeeVersions(map) {
  if (!map || typeof map !== 'object') return;
  for (const [id, hex] of Object.entries(map)) {
    if (typeof hex === 'string' && hex) versions.set(id, hex);
  }
}

/** The tokens for a set of ids, in the shape employees-write expects. */
export function employeeVersionsFor(ids) {
  const out = {};
  for (const id of ids || []) {
    const hex = versions.get(id);
    if (hex) out[id] = hex;
  }
  return out;
}

/** Drop a token — after a delete, or after a 409 made it worthless. */
export function forgetEmployeeVersion(id) {
  versions.delete(id);
}

/** Test seam. */
export function _resetEmployeeVersions() {
  versions.clear();
}
